import { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, Loader2, Check, X, Pencil, CalendarClock, UserCog, KeyRound, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { JaritBadge } from "@/components/JaritBadge";
import { EmployeeName } from "@/components/EmployeeName";
import { useT } from "@/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const resultLabel = (t, action, dv) =>
  action === "ignore" ? t("appr.ignore") : dv === 1 ? t("appr.fullDay") : dv === 0.5 ? t("appr.halfDay") : t("appr.zeroDay");
const niceResult = (s) => (s || "—").replace(/_/g, " ");

const GreenBtn = ({ onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex h-8 items-center gap-1 rounded-full bg-[#128C3E] px-3 text-[12.5px] font-medium text-white transition-all hover:bg-[#0F7433] active:scale-[0.98]"><Check className="h-3.5 w-3.5" /> {children}</button>
);
const RedBtn = ({ onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex h-8 items-center gap-1 rounded-full bg-[#C8102E] px-3 text-[12.5px] font-medium text-white transition-all hover:bg-[#A50D26] active:scale-[0.98]"><X className="h-3.5 w-3.5" /> {children}</button>
);

const Section = ({ icon: Icon, title, count, children }) => (
  <div className="liquid-glass-card p-4 sm:p-5">
    <div className="mb-3 flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#0071E3]" />
      <h2 className="text-[14px] font-semibold tracking-tight">{title}</h2>
      <span className="rounded-full bg-[#F2F2F7] px-2 py-0.5 text-[11.5px] font-medium text-[#6E6E73]">{count}</span>
    </div>
    {children}
  </div>
);

export default function ApprovalCenter() {
  const { t } = useT();
  const { readOnly } = useAuth();
  const [att, setAtt] = useState([]);
  const [access, setAccess] = useState([]);
  const [creds, setCreds] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // { req, action, day_value, note }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, ea, pw, pe] = await Promise.all([
        api.get("/attendance/requests", { params: { status: "pending" } }),
        api.get("/employee-access"),
        api.get("/password-requests"),
        api.get("/periods/edit-requests"),
      ]);
      setAtt(a.data || []);
      setAccess(ea.data?.pending || []);
      setCreds(pw.data || []);
      setPeriods(pe.data || []);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const total = att.length + access.length + creds.length + periods.length;

  const approveAtt = async (id, override) => {
    try { await api.post(`/attendance/requests/${id}/approve`, override || {}); toast.success(t("appr.approved")); setEdit(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const rejectAtt = async (id) => { try { await api.post(`/attendance/requests/${id}/reject`, {}); toast.success(t("appr.rejected")); load(); } catch (e) { toast.error(errMsg(e)); } };
  const decideAccess = async (id, approve) => { try { await api.post(`/employee-access/${id}/decision`, { approve }); toast.success(approve ? t("appr.approved") : t("appr.rejected")); load(); } catch (e) { toast.error(errMsg(e)); } };
  const approvePwd = async (id) => { try { await api.post(`/password-requests/${id}/approve`); toast.success(t("appr.approved")); load(); } catch (e) { toast.error(errMsg(e)); } };
  const rejectPwd = async (id) => { try { await api.post(`/password-requests/${id}/reject`); toast.success(t("appr.rejected")); load(); } catch (e) { toast.error(errMsg(e)); } };
  const approvePeriod = async (id) => { try { await api.post(`/periods/${id}/approve-edit`); toast.success(t("appr.approved")); load(); } catch (e) { toast.error(errMsg(e)); } };
  const rejectPeriod = async (id) => { try { await api.post(`/periods/${id}/reject-edit`); toast.success(t("appr.rejected")); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div data-testid="approval-center-page" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-[22px] font-semibold tracking-tight sm:text-[28px]">
          <ClipboardCheck className="h-6 w-6 text-[#0071E3]" /> {t("appr.title")}
        </h1>
        <p className="mt-0.5 text-[13px] text-[#8E8E93]">
          {total === 0 ? t("appr.none") : t("appr.pendingN", { n: total })}
        </p>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B0B0B8]" /></div>
      ) : total === 0 ? (
        <div data-testid="approval-empty" className="liquid-glass-card grid place-items-center py-16 text-center text-[13.5px] text-[#8E8E93]">
          {t("appr.nothing")}
        </div>
      ) : (
        <div className="space-y-4">
          {att.length > 0 && (
            <Section icon={CalendarClock} title={t("appr.attTitle")} count={att.length}>
              <div className="space-y-2.5">
                {att.map((r) => (
                  <div key={r.id} data-testid={`att-req-${r.id}`} className="rounded-2xl border border-black/[0.05] bg-white/70 p-3.5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex min-w-0 items-start gap-3">
                        <EmployeeAvatar id={r.employee_id} nama={r.employee_name} hasPhoto={r.has_photo} size={38} />
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 text-[14px] font-semibold text-[#1D1D1F]"><EmployeeName id={r.employee_id} name={r.employee_name} /><JaritBadge show={r.is_jarit} /></p>
                          <p className="num text-[11.5px] text-[#8E8E93]">{r.employee_nip || "—"} · {r.attendance_date}</p>
                          <p className="mt-1 text-[12.5px] text-[#3A3A3C]">
                            <span className="text-[#8E8E93]">{t("appr.current")}</span> {niceResult(r.current_result)}
                            <span className="mx-1 text-[#C7C7CC]">→</span>
                            <span className="font-medium text-[#0071E3]">{resultLabel(t, r.action, r.proposed_day_value)}</span>
                          </p>
                          <p className="mt-1 text-[12.5px] text-[#6E6E73]">“{r.reason}”</p>
                          <p className="mt-0.5 text-[11px] text-[#A1A1A6]">{t("appr.requestedBy", { name: r.requested_by })}</p>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        {!readOnly && (<>
                        <GreenBtn testid={`att-req-approve-${r.id}`} onClick={() => approveAtt(r.id)}>{t("appr.approve")}</GreenBtn>
                        <button data-testid={`att-req-edit-${r.id}`} onClick={() => setEdit({ req: r, action: r.action, day_value: r.proposed_day_value ?? 1, note: "" })} className="flex h-8 items-center gap-1 rounded-full border border-[#E1E1E6] bg-white px-3 text-[12.5px] font-medium text-[#0071E3] transition-all active:scale-[0.98]"><Pencil className="h-3.5 w-3.5" /> {t("appr.editApprove")}</button>
                        <RedBtn testid={`att-req-reject-${r.id}`} onClick={() => rejectAtt(r.id)}>{t("appr.reject")}</RedBtn>
                        </>)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {periods.length > 0 && (
            <Section icon={Lock} title={t("appr.periodTitle")} count={periods.length}>
              <div className="space-y-2.5">
                {periods.map((p) => (
                  <div key={p.id} data-testid={`period-req-${p.id}`} className="flex flex-col gap-3 rounded-2xl border border-black/[0.05] bg-white/70 p-3.5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-medium text-[#1D1D1F]">{p.label}</p>
                      <p className="text-[11.5px] text-[#8E8E93]">{t("appr.periodDesc", { label: p.label })}</p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {!readOnly && (<>
                      <GreenBtn testid={`period-approve-${p.id}`} onClick={() => approvePeriod(p.id)}>{t("appr.approve")}</GreenBtn>
                      <RedBtn testid={`period-reject-${p.id}`} onClick={() => rejectPeriod(p.id)}>{t("appr.reject")}</RedBtn>
                      </>)}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {access.length > 0 && (
            <Section icon={UserCog} title={t("appr.accessTitle")} count={access.length}>
              <div className="space-y-2.5">
                {access.map((p) => (
                  <div key={p.id} data-testid={`access-req-${p.id}`} className="flex flex-col gap-3 rounded-2xl border border-black/[0.05] bg-white/70 p-3.5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-medium text-[#1D1D1F]">{t("appr.addEditEmp")}</p>
                      <p className="text-[11.5px] text-[#8E8E93]">{t("appr.requestedBy", { name: p.requested_by })}</p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {!readOnly && (<>
                      <GreenBtn testid={`access-approve-${p.id}`} onClick={() => decideAccess(p.id, true)}>{t("appr.approve")}</GreenBtn>
                      <RedBtn testid={`access-reject-${p.id}`} onClick={() => decideAccess(p.id, false)}>{t("appr.reject")}</RedBtn>
                      </>)}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {creds.length > 0 && (
            <Section icon={KeyRound} title={t("appr.credTitle")} count={creds.length}>
              <div className="space-y-2.5">
                {creds.map((c) => (
                  <div key={c.id} data-testid={`cred-req-${c.id}`} className="flex flex-col gap-3 rounded-2xl border border-black/[0.05] bg-white/70 p-3.5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-[13.5px] font-medium text-[#1D1D1F]">{c.name || c.username}</p>
                      <p className="text-[11.5px] text-[#8E8E93]">
                        {c.new_username ? t("appr.newUsername", { u: c.new_username }) : ""}{c.new_username && c.has_password ? " · " : ""}{c.has_password ? t("appr.pwdChange") : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      {!readOnly && (<>
                      <GreenBtn testid={`cred-approve-${c.id}`} onClick={() => approvePwd(c.id)}>{t("appr.approve")}</GreenBtn>
                      <RedBtn testid={`cred-reject-${c.id}`} onClick={() => rejectPwd(c.id)}>{t("appr.reject")}</RedBtn>
                      </>)}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent data-testid="att-req-edit-dialog" className="gap-0 rounded-[20px] border border-white/50 bg-white/90 p-6 backdrop-blur-2xl sm:max-w-[420px]">
          <DialogHeader className="text-left"><DialogTitle className="text-[17px] font-semibold">{t("appr.editDialogTitle")}</DialogTitle></DialogHeader>
          {edit && (
            <div className="mt-3 space-y-3">
              <p className="text-[13px] text-[#6E6E73]">{edit.req.employee_name} · {edit.req.attendance_date}</p>
              <Select value={edit.action === "ignore" ? "ignore" : String(edit.day_value)} onValueChange={(v) => setEdit((e) => ({ ...e, action: v === "ignore" ? "ignore" : "approve", day_value: v === "ignore" ? 0 : Number(v) }))}>
                <SelectTrigger data-testid="att-req-edit-value" className="h-11 rounded-xl border-[#E1E1E6] bg-white/70 text-[14px]"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="1">{t("appr.dayFull")}</SelectItem>
                  <SelectItem value="0.5">{t("appr.dayHalf")}</SelectItem>
                  <SelectItem value="0">{t("appr.dayZero")}</SelectItem>
                  <SelectItem value="ignore">{t("appr.ignore")}</SelectItem>
                </SelectContent>
              </Select>
              <input data-testid="att-req-edit-note" value={edit.note} onChange={(e) => setEdit((x) => ({ ...x, note: e.target.value }))} placeholder={t("appr.adminNote")} className="h-11 w-full rounded-xl border border-[#E1E1E6] bg-white/70 px-3 text-[14px]" />
              <button data-testid="att-req-edit-confirm" onClick={() => approveAtt(edit.req.id, { action: edit.action, day_value: edit.day_value, admin_note: edit.note })} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#128C3E] text-[15px] font-medium text-white"><Check className="h-4 w-4" /> {t("appr.approveWith")}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
